import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  useToast,
  Card,
  CardBody,
  Button,
  Flex,
  Spinner,
  useColorModeValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  IconButton,
  Badge,
  HStack,
  Center,
  Icon,
  useColorMode,
  useToken,
  Tag,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { FiArrowRight, FiTrash2, FiMessageSquare, FiCheckCircle, FiBookOpen } from 'react-icons/fi';
import { GiTeacher } from 'react-icons/gi';
import { BsMortarboard } from 'react-icons/bs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from "../lib/apiClient";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { colors } from "../assets/styles/colors";
import logger from "../utils/logger";
import { ChatInterface } from "../components/chat/ChatInterface";

// Types
interface Word {
  id: string;
  word: string;
  language: string;
  translation?: string;
  translation_language?: string;
  user_id: string;
}

interface ChatMessage {
  role: "user" | "assistant" | "system";
  content: string;
}

interface LessonReport {
  id?: string;
  user_id: string;
  created_at?: string;
  title: string;
  summary: string;
  feedback: string;
  words_practiced: string[];
  word_ids: string[];
  conversation_history?: ChatMessage[];
  score?: number;
  duration?: number;
}

interface UserSettings {
  user_id: string;
  settings: {
    darkMode: boolean;
    dailyGoal: number;
    notifications: boolean;
    nativeLanguage: string;
    targetLanguage: string;
  };
  created_at: string;
  updated_at: string;
}

// Animation keyframes
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0px); }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const glowAnimation = keyframes`
  0% { box-shadow: 0 0 5px rgba(139, 92, 246, 0.5); }
  50% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.8); }
  100% { box-shadow: 0 0 5px rgba(139, 92, 246, 0.5); }
`;

export default function SmartPractice() {
  // Auth and utility hooks first - KEEP THESE IN THE EXACT SAME ORDER
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const { applyUserLanguage } = useLanguage();

  // Get color tokens
  const [primary500] = useToken("colors", ["brand.500"]);

  // Define UI color mode values before any conditional rendering
  const cardBg = useColorModeValue("white", colors.primary[700]);
  const cardColor = useColorModeValue("gray.800", "white");
  const borderColor = useColorModeValue("gray.200", colors.primary[600]);
  const modalHeaderBg = useColorModeValue(
    colors.primary[50],
    colors.primary[700],
  );
  const reportBg = useColorModeValue("white", colors.primary[700]);
  const wordBg = useColorModeValue("blue.50", "blue.900");
  const wordColor = useColorModeValue("blue.800", "blue.100");
  const messageBgUser = useColorModeValue("blue.100", "blue.700");
  const messageBgAi = useColorModeValue("gray.100", colors.primary[600]);
  const accentGradient = `linear-gradient(135deg, ${colors.primary[400]}, ${colors.secondary[400]})`;
  const buttonHoverBg = useColorModeValue(
    colors.primary[100],
    colors.primary[700],
  );
  const shadowColor = useColorModeValue(
    "rgba(0, 0, 0, 0.1)",
    "rgba(255, 255, 255, 0.1)",
  );

  // Define all border radius values used in the component
  const messageBorderRadius = useMemo(() => {
    return {
      user: "2xl 2xl 0 2xl",
      ai: "0 2xl 2xl 2xl",
    };
  }, []);

  // Define scrollbar colors separately to avoid hooks inside hooks
  const scrollbarTrackBg = useColorModeValue("gray.100", "gray.700");
  const scrollbarThumbBg = useColorModeValue("gray.300", "gray.600");
  const scrollbarThumbHoverBg = useColorModeValue("gray.400", "gray.500");

  // Use useMemo for chatContainerStyles to avoid recalculating on every render
  const chatContainerStyles = useMemo(
    () => ({
      "&::-webkit-scrollbar": {
        width: "8px",
        borderRadius: "8px",
      },
      "&::-webkit-scrollbar-track": {
        background: scrollbarTrackBg,
        borderRadius: "8px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: scrollbarThumbBg,
        borderRadius: "8px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: scrollbarThumbHoverBg,
      },
    }),
    [scrollbarTrackBg, scrollbarThumbBg, scrollbarThumbHoverBg],
  );

  // State hooks - KEEP THESE IN THE EXACT SAME ORDER
  const [isChatActive, setIsChatActive] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [lessonReport, setLessonReport] = useState<LessonReport | null>(null);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [sessionScore, setSessionScore] = useState(0);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [targetLanguage, setTargetLanguage] = useState<string>("");
  const [nativeLanguage, setNativeLanguage] = useState<string>("");
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [previousTargetLanguage, setPreviousTargetLanguage] =
    useState<string>("");
  const [languageChangeDetected, setLanguageChangeDetected] =
    useState<boolean>(false);
  const [isRTL, setIsRTL] = useState<boolean>(false);
  const [wordsUsedInSession, setWordsUsedInSession] = useState<string[]>([]);
  const [wordIdsUsedInSession, setWordIdsUsedInSession] = useState<string[]>(
    [],
  );
  const [showAllReports, setShowAllReports] = useState(false);

  // Query hooks - KEEP THESE IN THE EXACT SAME ORDER
  const { data: userSettings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ["user-settings"],
    queryFn: async () => {
      if (!user?.id) return null;

      try {
        const settings = await apiClient.getUserSettings();

        if (!settings) {
          logger.error("No user settings found");
          return null;
        }

        return settings as UserSettings;
      } catch (error) {
        logger.error("Error in user settings query:", error);
        return null;
      }
    },
    enabled: !!user?.id,
  });

  // Get words for the user in the target language
  const { data: words, isLoading: isLoadingWords } = useQuery({
    queryKey: ["words", user?.id, targetLanguage],
    queryFn: async () => {
      if (!user?.id || !targetLanguage) return [];

      try {
        const data = await apiClient.getWords(targetLanguage.toLowerCase());
        return data as unknown as Word[];
      } catch (error) {
        logger.error("Error in words query:", error);
        return [];
      }
    },
    enabled: !!user?.id && !!targetLanguage,
  });

  // Get accurate count of words for the user in the target language
  const { data: wordsCount } = useQuery({
    queryKey: ["words-count", user?.id, targetLanguage],
    queryFn: async () => {
      if (!user?.id || !targetLanguage) return 0;
      if (words) return words.length;

      try {
        const data = await apiClient.getWords(targetLanguage.toLowerCase());
        return data?.length || 0;
      } catch (error) {
        logger.error("Error in words count query:", error);
        return 0;
      }
    },
    enabled: !!user?.id && !!targetLanguage,
  });

  // Get previous lesson reports
  const { data: lessonReports, isLoading: isLoadingReports } = useQuery({
    queryKey: ["lesson-reports", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      try {
        const data = await apiClient.getLessonReports();
        return data as unknown as LessonReport[];
      } catch (error) {
        logger.error("Error in lesson reports query:", error);
        return [];
      }
    },
    enabled: !!user?.id,
  });

  // Save lesson report mutation
  const saveLessonReportMutation = useMutation({
    mutationFn: async (report: LessonReport) => {
      try {
        const savedReport = await apiClient.saveLessonReport(report);
        return savedReport;
      } catch (error) {
        logger.error("Error in saveLessonReportMutation:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lesson-reports"] });
    },
  });

  // Delete lesson report mutation
  const deleteLessonReportMutation = useMutation({
    mutationFn: async (reportId: string) => {
      try {
        await apiClient.deleteLessonReport(reportId);
        return reportId;
      } catch (error) {
        throw new Error(`Failed to delete lesson report: ${error}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lesson-reports"] });
      handleCloseModal(); // Use the new handler to also reset state
    },
  });

  // Check for language settings on mount
  useEffect(() => {
    if (userSettings?.settings) {
      setTargetLanguage(userSettings.settings.targetLanguage || "Dutch");
      setNativeLanguage(userSettings.settings.nativeLanguage || "English");

      // Set application language based on user's native language
      applyUserLanguage(userSettings.settings.nativeLanguage || "English");

      // Check if the target language is RTL
      const rtlLangs = ["hebrew", "arabic"];
      setIsRTL(
        rtlLangs.includes(
          (userSettings.settings.targetLanguage || "").toLowerCase(),
        ),
      );
    }
  }, [userSettings, applyUserLanguage]);

  // Start a new practice session
  const startPractice = async () => {
    setIsChatActive(true);
    setSessionStartTime(Date.now());
    setSessionScore(0);
    if (!words || words.length === 0) {
      toast({
        title: "No Words",
        description: t("smartPractice.addWordsFirst"),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (previousTargetLanguage && previousTargetLanguage !== targetLanguage) {
      // Store the new target language and show the language change notice
      setPreviousTargetLanguage(targetLanguage);
      setLanguageChangeDetected(true);
    } else {
      // First time or same language, just store it
      setPreviousTargetLanguage(targetLanguage);
    }

    // Reset the chat
    setChatMessages([]);
  };

  // Define onClose handler for modal, resetting selectedReportId
  const handleCloseModal = () => {
    setSelectedReportId(null); // Reset report ID when closing
    onClose();
  };

  // End practice session and generate report
  const endPractice = async () => {
    // Don't set isChatActive to false yet - keep the chat visible but disable input
    setIsGeneratingReport(true);

    // Use an AbortController to handle timeouts
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    try {
      // Get current target language even if variable updated during async operation
      const currentTargetLanguage = targetLanguage;

      // Add a special message to generate a summary
      const summaryRequest: ChatMessage = {
        role: "user",
        content: `Please provide a comprehensive summary of this practice session. Include:
1. Specific sentences I created and what I did well
2. Grammar or vocabulary mistakes I made and how to fix them
3. Concrete personalized tips for improving my ${targetLanguage} skills
4. A brief highlight of words I used effectively
5. Suggested areas to focus on next

Keep your feedback specific to our conversation, be encouraging but honest about areas for improvement. Focus on concrete, actionable advice rather than generic tips.`,
      };

      const messagesForReport = [...chatMessages, summaryRequest];

      // Use apiClient to generate report instead of direct Supabase call
      logger.info("Making report generation API call to AI proxy");

      const reportResponse = await apiClient.generateSmartPracticeReport({
        messages: messagesForReport,
        words: words || [],
        targetLanguage,
        nativeLanguage,
        timestamp: new Date().toISOString(),
        words_used: wordsUsedInSession,
      });

      const summaryContent =
        reportResponse.choices?.[0]?.message?.content || reportResponse.content;

      if (!summaryContent) {
        throw new Error("Invalid response format from report generation");
      }

      // Filter out system messages before saving conversation history
      const visibleMessages = chatMessages.filter(
        (msg) => msg.role !== "system",
      );

      // Use words that were actually used in the session
      const usedWordsData =
        wordIdsUsedInSession.length > 0
          ? {
              words_practiced: wordsUsedInSession,
              word_ids: wordIdsUsedInSession,
            }
          : {
              // Fallback if no words were tracked
              words_practiced: words?.slice(0, 10).map((w) => w.word) || [],
              word_ids: words?.slice(0, 10).map((w) => w.id) || [],
            };

      // Add session stats to the report
      const sessionStats = {
        score: sessionScore,
        duration: sessionStartTime
          ? Math.floor((Date.now() - sessionStartTime) / 1000)
          : 0,
      };

      // Create report object
      const report: LessonReport = {
        user_id: user?.id || "",
        title: `${
          currentTargetLanguage.charAt(0).toUpperCase() +
          currentTargetLanguage.slice(1)
        } Practice Session`,
        summary: summaryContent,
        feedback: summaryContent,
        ...usedWordsData,
        ...sessionStats,
        conversation_history: visibleMessages,
      };

      // Set the report in state and open modal immediately
      setLessonReport(report);
      onOpen();

      // Save report in background
      try {
        const savedReport = await saveLessonReportMutation.mutateAsync(report);
        logger.info("Lesson report saved successfully");

        // Update report state with ID once saved
        if (
          Array.isArray(savedReport) &&
          savedReport[0] &&
          typeof savedReport[0] === "object" &&
          "id" in savedReport[0]
        ) {
          setLessonReport((prev) =>
            prev
              ? { ...prev, id: (savedReport[0] as { id: string }).id }
              : null,
          );
          setSelectedReportId((savedReport[0] as { id: string }).id);
        }
      } catch (saveError) {
        logger.error("Error saving lesson report:", saveError);
        // Don't set selectedReportId since save failed
        setSelectedReportId(null);

        // Show error toast but keep modal open
        toast({
          title: t("common.error"),
          description: t("smartPractice.errorSavingReport"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }

      // Only now set chat as inactive
      setIsChatActive(false);
    } catch (error) {
      logger.error("Error generating report:", error);

      // Use default target language if undefined
      const currentTargetLanguage = targetLanguage || "Dutch";

      // Check if it was an abort error
      if (error instanceof DOMException && error.name === "AbortError") {
        toast({
          title: "Report Generation Timeout",
          description:
            "Generating the report is taking longer than expected. Using a simplified report instead.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to generate your lesson report.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }

      // Filter out system messages before saving conversation history
      const visibleMessages = chatMessages.filter(
        (msg) => msg.role !== "system",
      );

      // Fall back to a basic report
      const fallbackReport: LessonReport = {
        user_id: user?.id || "",
        title: `${
          currentTargetLanguage.charAt(0).toUpperCase() +
          currentTargetLanguage.slice(1)
        } Practice Session`,
        summary: "A summary could not be generated for this practice session.",
        feedback: "Please try again later.",
        words_practiced: words?.slice(0, 5).map((w) => w.word) || [],
        word_ids: words?.slice(0, 5).map((w) => w.id) || [],
        score: sessionScore,
        duration: sessionStartTime
          ? Math.floor((Date.now() - sessionStartTime) / 1000)
          : 0,
        conversation_history: visibleMessages,
      };

      setLessonReport(fallbackReport);
      setSelectedReportId(null); // Ensure delete is disabled for fallback
      onOpen(); // Still show the modal with fallback report
      setIsChatActive(false);
    } finally {
      setIsGeneratingReport(false);
      clearTimeout(timeoutId);
      setSessionStartTime(null);
      setSessionScore(0);
    }
  };

  // Handle viewing a previous report
  const handleViewReport = (report: LessonReport) => {
    setLessonReport(report);
    setSelectedReportId(report.id ?? null); // Use ID if available
    onOpen();
  };

  // Handle deletion of a report
  const handleDeleteReportClick = (reportId: string) => {
    // This function might not be needed anymore if delete is only in modal
    // Kept for now in case it's used elsewhere
    setSelectedReportId(reportId);
    // onOpen(); // Let handleViewReport or endPractice open the modal
  };

  // Confirm deletion of report
  const handleDeleteReport = () => {
    if (selectedReportId) {
      deleteLessonReportMutation.mutate(selectedReportId);
      // onClose(); // Mutation onSuccess handles closing and state reset
    }
  };

  // Update chat messages from child component
  const handleUpdateChatMessages = (messages: ChatMessage[]) => {
    setChatMessages(messages);
  };

  // Track words used in conversation
  const handleWordUsed = (wordId: string, wordText: string) => {
    if (!wordIdsUsedInSession.includes(wordId)) {
      setWordIdsUsedInSession((prevIds) => [...prevIds, wordId]);
      setWordsUsedInSession((prevWords) => [...prevWords, wordText]);
    }
  };

  // Let's define a more vibrant gradient for the title
  const titleGradient = useColorModeValue(
    `linear(to-r, ${colors.accent.purple}, ${colors.accent.blue})`,
    `linear(to-r, ${colors.accent.purple}, ${colors.accent.blue})`
  );

  // Add/remove chat-active class to body when chat is active
  useEffect(() => {
    if (isChatActive) {
      document.body.classList.add("chat-active");
    } else {
      document.body.classList.remove("chat-active");
    }

    return () => {
      document.body.classList.remove("chat-active");
    };
  }, [isChatActive]);

  return (
    <Container
      maxW={{ base: "container.sm", md: "container.lg" }}
      py={isChatActive ? 0 : 8}
      px={isChatActive ? 0 : { base: 4, md: 8 }}
      position="relative"
      pb={isChatActive ? undefined : { base: "80px", md: "initial" }}
      height={isChatActive ? "100vh" : "auto"}
      overflow={isChatActive ? "hidden" : "visible"}
    >
      {!isChatActive ? (
        <>
          <Heading
            as="h1"
            display="flex"
            alignItems="center"
            bgGradient="linear(to-l, #7928CA, #FF0080)"
            bgClip="text"
            fontWeight="extrabold"
            fontSize={{ base: "2xl", md: "3xl" }}
            mb={{ base: 6, md: 8 }}
            textAlign="left"
          >
            <Icon as={FiMessageSquare} mr={2} />
            {t("smartPractice.title") || "Smart Practice"}
          </Heading>

          <VStack spacing={8} align="stretch">
            <Card
              bg={cardBg}
              color={cardColor}
              borderRadius="2xl"
              boxShadow="xl"
              borderWidth="1px"
              borderColor={borderColor}
              overflow="hidden"
              _hover={{
                transform: "translateY(-5px)",
                transition: "transform 0.3s ease",
                boxShadow: `0 15px 30px ${shadowColor}`,
              }}
              transition="all 0.3s ease"
            >
              <Box bg={accentGradient} h="8px" w="100%" />
              <CardBody p={8}>
                <VStack spacing={8} align="center">
                  <Box
                    p={4}
                    borderRadius="full"
                    bg={colors.primary[50]}
                    color={colors.primary[500]}
                    animation={`${floatAnimation} 3s ease-in-out infinite`}
                    boxShadow={`0 0 15px ${colors.primary[300]}`}
                  >
                    <Icon as={GiTeacher} boxSize={12} />
                  </Box>

                  <Heading size="lg" textAlign="center" fontWeight="bold">
                    {t("smartPractice.practiceWith")} {targetLanguage}
                  </Heading>

                  <Text textAlign="center" fontSize="lg">
                    {t("smartPractice.description")}
                  </Text>

                  <HStack
                    bg={colors.primary[50]}
                    p={4}
                    borderRadius="xl"
                    _dark={{ bg: colors.primary[900] }}
                  >
                    <Text fontWeight="bold">
                      {t("smartPractice.availableWords")}:
                    </Text>
                    <Badge
                      colorScheme={words && words.length > 0 ? "green" : "red"}
                      fontSize="md"
                      px={3}
                      py={1.5}
                      borderRadius="lg"
                      animation={
                        words && words.length > 0
                          ? `${pulseAnimation} 2s ease-in-out infinite`
                          : "none"
                      }
                    >
                      {isLoadingWords ? (
                        <Spinner size="xs" />
                      ) : (
                        (wordsCount ?? words?.length ?? 0)
                      )}
                    </Badge>
                  </HStack>

                  <Button
                    colorScheme="brand"
                    size="lg"
                    rightIcon={<FiArrowRight />}
                    onClick={startPractice}
                    isDisabled={
                      isLoadingWords || (wordsCount ?? words?.length ?? 0) === 0
                    }
                    _hover={{
                      transform: "translateY(-4px)",
                      boxShadow: "lg",
                      bg: colors.primary[400],
                    }}
                    _active={{
                      transform: "translateY(0)",
                      boxShadow: "md",
                    }}
                    fontWeight="bold"
                    px={10}
                    py={6}
                    fontSize="xl"
                    borderRadius="xl"
                    bgGradient={accentGradient}
                    transition="all 0.3s ease"
                    animation={`${glowAnimation} 3s infinite`}
                  >
                    {t("smartPractice.startPractice")}
                  </Button>

                  {(wordsCount ?? words?.length ?? 0) === 0 &&
                    !isLoadingWords && (
                      <Text color="red.500" fontSize="md" fontWeight="medium">
                        {t("smartPractice.addWordsFirst")}
                      </Text>
                    )}
                </VStack>
              </CardBody>
            </Card>

            {/* Previous Sessions Section */}
            {lessonReports && lessonReports.length > 0 ? (
              <Card
                bg={cardBg}
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="lg"
                borderWidth="1px"
                borderColor={borderColor}
                _hover={{
                  boxShadow: `0 10px 20px ${shadowColor}`,
                  transition: "all 0.3s ease",
                }}
              >
                <Box bg={accentGradient} h="8px" w="100%" />
                <CardBody p={6}>
                  <VStack spacing={6} align="stretch">
                    <Flex justify="space-between" align="center">
                      <Heading
                        size="md"
                        fontWeight="bold"
                        bgGradient={accentGradient}
                        bgClip="text"
                      >
                        {t("smartPractice.previousSessions")}
                      </Heading>
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="brand"
                        onClick={() => setShowAllReports(!showAllReports)}
                        _hover={{
                          bg: buttonHoverBg,
                        }}
                      >
                        {showAllReports
                          ? t("common.showLess")
                          : t("common.showAll")}
                      </Button>
                    </Flex>
                    <VStack spacing={4} align="stretch">
                      {[...lessonReports]
                        .reverse()
                        .slice(0, showAllReports ? undefined : 5)
                        .map((report) => (
                          <Box
                            key={report.id}
                            p={5}
                            borderRadius="xl"
                            bg={colors.primary[50]}
                            _dark={{ bg: colors.primary[900] }}
                            cursor="pointer"
                            onClick={() => handleViewReport(report)}
                            _hover={{
                              transform: "translateY(-4px)",
                              boxShadow: "md",
                              bg: colors.primary[100],
                              _dark: { bg: colors.primary[800] },
                            }}
                            transition="all 0.2s ease"
                            borderLeft="4px solid"
                            borderLeftColor={colors.primary[400]}
                          >
                            <Flex
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <VStack align="start" spacing={1}>
                                <Heading size="sm">{report.title}</Heading>
                                <HStack spacing={3}>
                                  <Text fontSize="xs" color="gray.500">
                                    {new Date(
                                      report.created_at || "",
                                    ).toLocaleDateString()}
                                  </Text>
                                  <Badge
                                    colorScheme="purple"
                                    borderRadius="full"
                                    px={2}
                                  >
                                    {report.words_practiced?.length || 0}{" "}
                                    {t("common.words").toLowerCase()}
                                  </Badge>
                                  {report.score && (
                                    <Badge
                                      colorScheme="green"
                                      borderRadius="full"
                                      px={2}
                                    >
                                      {report.score} {t("chat.points")}
                                    </Badge>
                                  )}
                                </HStack>
                              </VStack>
                              <IconButton
                                aria-label="View report"
                                icon={<FiMessageSquare />}
                                variant="ghost"
                                colorScheme="brand"
                                size="sm"
                                borderRadius="full"
                                _hover={{
                                  bg: colors.primary[100],
                                  _dark: { bg: colors.primary[800] },
                                }}
                              />
                            </Flex>
                          </Box>
                        ))}
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            ) : (
              <Card
                bg={cardBg}
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="lg"
                borderWidth="1px"
                borderColor={borderColor}
                _hover={{
                  boxShadow: `0 10px 20px ${shadowColor}`,
                  transition: "all 0.3s ease",
                }}
              >
                <Box bg={accentGradient} h="8px" w="100%" />
                <CardBody p={6}>
                  <VStack spacing={6} align="stretch">
                    <Heading
                      size="md"
                      fontWeight="bold"
                      bgGradient={accentGradient}
                      bgClip="text"
                    >
                      {t("smartPractice.previousSessions")}
                    </Heading>
                    <Center h="100%" py={12}>
                      <VStack spacing={6}>
                        <Box
                          p={5}
                          borderRadius="full"
                          bg={colors.primary[50]}
                          color={colors.primary[500]}
                          animation={`${floatAnimation} 3s ease-in-out infinite`}
                          boxShadow={`0 0 15px ${colors.primary[300]}`}
                        >
                          <Icon as={FiBookOpen} boxSize={12} />
                        </Box>
                        <Heading size="md" textAlign="center" fontWeight="bold">
                          {t("smartPractice.noSessionsYet") ||
                            "No sessions yet"}
                        </Heading>
                        <Text textAlign="center" color="gray.500" fontSize="lg">
                          {t("smartPractice.startFirstSession") ||
                            "Start your first session to see reports here"}
                        </Text>
                      </VStack>
                    </Center>
                  </VStack>
                </CardBody>
              </Card>
            )}
          </VStack>
        </>
      ) : (
        // Chat Active - Render ChatInterface directly within the flow
        <ChatInterface
          words={words || []}
          targetLanguage={targetLanguage}
          nativeLanguage={nativeLanguage}
          onEndSession={endPractice}
          onUpdateMessages={handleUpdateChatMessages}
          onWordUsed={handleWordUsed}
        />
      )}

      {/* Report Viewing Modal - Use handleCloseModal for onClose */}
      <Modal
        isOpen={isOpen}
        onClose={handleCloseModal}
        size="full"
        scrollBehavior="inside"
      >
        <ModalOverlay backdropFilter="blur(8px)" />
        <ModalContent borderRadius="xl" overflow="hidden">
          <ModalHeader
            borderBottomWidth="1px"
            borderColor={borderColor}
            bg={modalHeaderBg}
            py={5}
          >
            <Flex justify="space-between" align="center" w="100%">
              <HStack spacing={3}>
                <Box
                  p={2}
                  borderRadius="full"
                  bg={colors.primary[100]}
                  color={colors.primary[600]}
                  _dark={{
                    bg: colors.primary[800],
                    color: colors.primary[200],
                  }}
                >
                  <Icon as={BsMortarboard} boxSize="24px" />
                </Box>
                <Heading size="lg">
                  {lessonReport
                    ? lessonReport.title
                    : t("smartPractice.sessionReport")}
                </Heading>
              </HStack>
              <IconButton
                aria-label="Close"
                icon={<Icon as={FiCheckCircle} />}
                onClick={handleCloseModal}
                variant="ghost"
                colorScheme="brand"
                size="lg"
                borderRadius="full"
                _hover={{
                  bg: colors.primary[100],
                  _dark: { bg: colors.primary[800] },
                }}
              />
            </Flex>
          </ModalHeader>

          <ModalBody p={{ base: 4, md: 8 }}>
            {lessonReport && (
              <VStack spacing={8} align="stretch" maxW="container.lg" mx="auto">
                <Box>
                  <Heading
                    size="lg"
                    mb={4}
                    color={colors.primary[500]}
                    display="flex"
                    alignItems="center"
                    gap={3}
                  >
                    <Icon as={FiCheckCircle} />
                    {t("smartPractice.feedback")}
                  </Heading>
                  <Card
                    p={8}
                    bg={reportBg}
                    borderRadius="xl"
                    boxShadow="md"
                    borderLeft="4px solid"
                    borderLeftColor={colors.primary[400]}
                  >
                    <ReactMarkdown>{lessonReport.feedback}</ReactMarkdown>
                  </Card>
                </Box>

                {lessonReport.words_practiced &&
                  lessonReport.words_practiced.length > 0 && (
                    <Box>
                      <Heading
                        size="lg"
                        mb={4}
                        color={colors.primary[500]}
                        display="flex"
                        alignItems="center"
                        gap={3}
                      >
                        <Icon as={FiBookOpen} />
                        {t("smartPractice.wordsPracticed")}
                        <Badge
                          ml={3}
                          colorScheme="green"
                          borderRadius="full"
                          fontSize="md"
                          px={3}
                          py={1}
                          animation={`${pulseAnimation} 2s ease-in-out infinite`}
                        >
                          {lessonReport.words_practiced.length}
                        </Badge>
                      </Heading>
                      <Flex flexWrap="wrap" gap={3}>
                        {lessonReport.words_practiced.map((word, index) => (
                          <Tag
                            key={index}
                            size="lg"
                            variant="subtle"
                            bg={wordBg}
                            color={wordColor}
                            borderRadius="full"
                            py={2}
                            px={4}
                            boxShadow="sm"
                            _hover={{
                              transform: "translateY(-2px)",
                              boxShadow: "md",
                            }}
                            transition="all 0.2s ease"
                          >
                            {word}
                          </Tag>
                        ))}
                      </Flex>
                    </Box>
                  )}

                {lessonReport.conversation_history &&
                lessonReport.conversation_history.length > 0 ? (
                  <Box>
                    <Heading
                      size="lg"
                      mb={4}
                      color={colors.primary[500]}
                      display="flex"
                      alignItems="center"
                      gap={3}
                    >
                      <Icon as={FiMessageSquare} />
                      {t("smartPractice.conversationHistory")}
                    </Heading>
                    <Card
                      p={6}
                      bg={reportBg}
                      borderRadius="xl"
                      boxShadow="md"
                      maxH="60vh"
                      overflowY="auto"
                      sx={chatContainerStyles}
                      borderLeft="4px solid"
                      borderLeftColor={colors.primary[400]}
                    >
                      <VStack spacing={4} align="stretch">
                        {lessonReport.conversation_history.map(
                          (message, index) => (
                            <Box
                              key={index}
                              bg={
                                message.role === "user"
                                  ? messageBgUser
                                  : messageBgAi
                              }
                              p={4}
                              borderRadius={
                                message.role === "user"
                                  ? messageBorderRadius.user
                                  : messageBorderRadius.ai
                              }
                              alignSelf={
                                message.role === "user"
                                  ? "flex-end"
                                  : "flex-start"
                              }
                              maxW={{ base: "90%", md: "80%" }}
                              boxShadow="sm"
                            >
                              <Text fontWeight="bold" fontSize="md" mb={2}>
                                {message.role === "user"
                                  ? t("common.you") || "You"
                                  : t("smartPractice.aiTutor")}
                              </Text>
                              <Text fontSize="md">{message.content}</Text>
                            </Box>
                          ),
                        )}
                      </VStack>
                    </Card>
                  </Box>
                ) : null}
              </VStack>
            )}
          </ModalBody>

          <ModalFooter
            borderTopWidth="1px"
            borderColor={borderColor}
            justifyContent="space-between"
            py={4}
          >
            <Button
              variant="outline"
              colorScheme="red"
              onClick={() => handleDeleteReport()} // Calls the delete mutation
              leftIcon={<FiTrash2 />}
              size="lg"
              // Disable if no report ID is selected for deletion
              isDisabled={!selectedReportId}
              borderRadius="lg"
              _hover={{
                bg: "red.50",
                _dark: { bg: "red.900" },
              }}
            >
              {t("common.delete")}
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleCloseModal} // Use the new handler
              leftIcon={<FiCheckCircle />}
              size="lg"
              borderRadius="lg"
              bgGradient={accentGradient}
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "lg",
              }}
              transition="all 0.2s ease"
            >
              {t("common.close")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
}
